import { useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  ChatCircle,
  Drop,
  Heartbeat,
  UsersThree,
  DotsThreeOutline,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../LoginModal';
import MorePopover from './MorePopover';
import DemoResetModal from '../DemoResetModal';

const TABS_LEFT = [
  { to: '/health', label: 'Health', icon: Heartbeat, matchPrefix: '/health' },
  { to: '/health/cycle', label: 'Cycle', icon: Drop, matchPrefix: '/health/cycle' },
] as const;

const TABS_RIGHT = [
  { to: '/community', label: 'Community', icon: UsersThree, matchPrefix: '/community' },
] as const;

const MORE_PREFIXES = ['/about', '/advocacy', '/trials', '/trust', '/settings', '/research'];

// All known prefixes — used so the most specific match wins (e.g. /health/cycle beats /health)
const ALL_PREFIXES = [
  ...TABS_LEFT.map(t => t.matchPrefix),
  ...TABS_RIGHT.map(t => t.matchPrefix),
  ...MORE_PREFIXES,
];

function isTabActive(pathname: string, matchPrefix: string): boolean {
  if (!pathname.startsWith(matchPrefix)) return false;
  // If a longer (more specific) prefix also matches, this tab isn't the right one
  return !ALL_PREFIXES.some(p => p !== matchPrefix && p.length > matchPrefix.length && pathname.startsWith(p));
}

export default function BottomTabBar() {
  const { currentUser, isDemoAccount } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  // Triple-tap detection for demo accounts
  const tapTimesRef = useRef<number[]>([]);
  const handleRomyTap = useCallback(() => {
    if (!isDemoAccount) return false;
    const now = Date.now();
    tapTimesRef.current.push(now);
    // Keep only the last 3 taps
    if (tapTimesRef.current.length > 3) tapTimesRef.current.shift();
    // Check if 3 taps within 800ms
    if (tapTimesRef.current.length === 3) {
      const elapsed = now - tapTimesRef.current[0];
      if (elapsed < 800) {
        tapTimesRef.current = [];
        setIsDemoModalOpen(true);
        return true; // triple-tap detected
      }
    }
    return false;
  }, [isDemoAccount]);

  const isNative = Capacitor.isNativePlatform();
  const isMoreActive =
    moreOpen || MORE_PREFIXES.some((p) => location.pathname.startsWith(p));
  const isChatActive =
    location.pathname.startsWith('/chat') ||
    (location.pathname === '/' && (isNative || !!currentUser));

  return (
    <>
      <nav
        className={`${isNative ? '' : 'md:hidden'} fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 overflow-visible`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center justify-around h-14">
          {/* Left tabs */}
          {TABS_LEFT.map(({ to, label, icon: Icon, matchPrefix }) => {
            const active = isTabActive(location.pathname, matchPrefix);
            return (
              <Link
                key={to}
                to={to}
                className="flex flex-col items-center justify-center flex-1 h-full"
              >
                <Icon
                  size={22}
                  weight={active ? 'fill' : 'regular'}
                  className={active ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
                />
                <span
                  className={`text-[10px] mt-0.5 ${
                    active ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {label}
                </span>
              </Link>
            );
          })}

          {/* Center Chat / Romy button — raised */}
          <button
            onClick={() => {
              // Check for triple-tap on demo accounts first
              if (handleRomyTap()) return;
              if (isChatActive) {
                // Try to send; Chat.tsx listens and calls handleSend if there's input
                window.dispatchEvent(new CustomEvent('chat-send'));
              } else {
                navigate('/chat');
              }
            }}
            className="flex flex-col items-center justify-center flex-1"
          >
            <div
              className={`flex items-center justify-center w-14 h-14 -mt-4 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 shadow-lg ring-2 ring-white dark:ring-gray-900`}
            >
              <ChatCircle size={26} weight="fill" className="text-white" />
            </div>
            <span
              className={`text-[10px] mt-0.5 ${
                isChatActive ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              Romy
            </span>
          </button>

          {/* Right tabs */}
          {TABS_RIGHT.map(({ to, label, icon: Icon, matchPrefix }) => {
            const active = isTabActive(location.pathname, matchPrefix);
            return (
              <Link
                key={to}
                to={to}
                className="flex flex-col items-center justify-center flex-1 h-full"
              >
                <Icon
                  size={22}
                  weight={active ? 'fill' : 'regular'}
                  className={active ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
                />
                <span
                  className={`text-[10px] mt-0.5 ${
                    active ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {label}
                </span>
              </Link>
            );
          })}

          {/* More tab */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className="flex flex-col items-center justify-center flex-1 h-full"
          >
            <DotsThreeOutline
              size={22}
              weight={isMoreActive ? 'fill' : 'regular'}
              className={isMoreActive ? 'text-pink-600 dark:text-pink-400' : 'text-gray-500 dark:text-gray-400'}
            />
            <span
              className={`text-[10px] mt-0.5 ${
                isMoreActive ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              More
            </span>
          </button>
        </div>
      </nav>

      <MorePopover
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        onSignInClick={() => setIsLoginOpen(true)}
      />

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />

      {isDemoAccount && (
        <DemoResetModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
      )}
    </>
  );
}
